<template>
  <div v-if="languageCodes.length > 1" class="language-dropdown">
    <button class="dropdown-button" @click="toggleDropdown">
      <img
        :src="getFlag(this.$store.getters.language)"
        alt="Flag"
        class="flag-icon"
      />
      <span class="language-name">{{
        getLanguageName(this.$store.getters.language)
      }}</span>
      <img
        class="arrow"
        :class="{ rotate: dropdownOpen }"
        src="/arrows/open.svg"
      />
    </button>

    <ul v-if="dropdownOpen" class="dropdown-menu">
      <li
        v-for="(language, code) in languages"
        :key="code"
        @click="changeLanguage(code)"
        class="dropdown-item"
      >
        <img :src="getFlag(code)" alt="Flag" class="flag-icon" />
        <span class="language-name">{{ getLanguageName(code) }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
const languages = {
  de: {
    de: "Deutsch",
    en: "German",
    fr: "Allemand",
    it: "Tedesco",
    nl: "Duits",
    cs: "Němčina",
  },
  en: {
    de: "Englisch",
    en: "English",
    fr: "Anglais",
    it: "Inglese",
    nl: "Engels",
    cs: "Angličtina",
  },
  fr: {
    de: "Französisch",
    en: "French",
    fr: "Français",
    it: "Francese",
    nl: "Frans",
    cs: "Francouzština",
  },
  it: {
    de: "Italienisch",
    en: "Italian",
    fr: "Italien",
    it: "Italiano",
    nl: "Italiaans",
    cs: "Italština",
  },
  nl: {
    de: "Niederländisch",
    en: "Dutch",
    fr: "Néerlandais",
    it: "Olandese",
    nl: "Nederlands",
    cs: "Nizozemština",
  },
  cs: {
    de: "Tschechisch",
    en: "Czech",
    fr: "Tchèque",
    it: "Ceco",
    nl: "Tsjechisch",
    cs: "Čeština",
  },
};

export default {
  data() {
    return {
      dropdownOpen: false,
    };
  },
  computed: {
    languages() {
      const availableLanguages =
        this.$store.getters.subdomainConfig?.availableLanguages;

      if (!Array.isArray(availableLanguages) || !availableLanguages.length) {
        return languages;
      }

      const filteredLanguages = availableLanguages.reduce(
        (filteredLanguages, code) => {
          if (languages[code]) {
            filteredLanguages[code] = languages[code];
          }

          return filteredLanguages;
        },
        {}
      );

      return Object.keys(filteredLanguages).length
        ? filteredLanguages
        : languages;
    },
    languageCodes() {
      return Object.keys(this.languages);
    },
    defaultLanguage() {
      const configuredDefaultLanguage =
        this.$store.getters.subdomainConfig?.defaultLanguage;

      if (this.languages[configuredDefaultLanguage]) {
        return configuredDefaultLanguage;
      }

      return this.languageCodes[0] || "de";
    },
  },
  watch: {
    languages() {
      this.ensureAllowedLanguage();
    },
  },
  methods: {
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },

    changeLanguage(code) {
      if (!this.languages[code]) {
        return;
      }

      this.$store.commit("setLanguage", code);
      this.$store.dispatch("getProducts");
      this.$i18n.locale = code;
      this.dropdownOpen = false;
      const url = new URL(window.location.href);
      url.searchParams.set("lang", code);
      window.history.replaceState({}, "", url);
    },

    getFlag(code) {
      return `/flags/${code}.svg`;
    },

    getLanguageName(languageCode) {
      const language =
        this.languages[languageCode] || this.languages[this.defaultLanguage];
      const activeLanguage = this.languages[this.$store.getters.language]
        ? this.$store.getters.language
        : this.defaultLanguage;

      return language?.[activeLanguage] || "";
    },

    ensureAllowedLanguage() {
      if (!this.languages[this.$store.getters.language]) {
        this.changeLanguage(this.defaultLanguage);
      }
    },

    loadLanguageFromURL() {
      let legacyLang = window.location.pathname.split("/")[1];
      if (
        legacyLang === "de" ||
        legacyLang === "fr" ||
        legacyLang === "it" ||
        legacyLang === "en" ||
        legacyLang === "nl" ||
        legacyLang === "cs"
      ) {
        this.changeLanguage(legacyLang);
        window.location.pathname = window.location.pathname.split("/")[0];
        return;
      }
      const urlParams = new URLSearchParams(window.location.search);
      const lang = urlParams.get("lang");

      if (lang && Object.keys(this.languages).includes(lang)) {
        this.changeLanguage(lang);
      } else {
        this.changeLanguage(this.defaultLanguage);
      }
    },
  },
  mounted() {
    this.loadLanguageFromURL();
    this.ensureAllowedLanguage();
  },
};
</script>

<style>
.language-dropdown {
  position: relative;
  display: inline-block;
}

.dropdown-button {
  display: flex;
  align-items: center;
  background-color: #ffffff;
  border: none;
  border-radius: 5px;
  padding: 7.5px 5px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dropdown-button:hover {
  background-color: #f3f3f3;
}

.flag-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-right: 10px;
}

@media (max-width: 815px) {
  .language-name {
    display: none;
  }
}

.language-name {
  font-size: 16px;
  font-weight: 500;
  margin-right: 10px;
  color: #333;
  white-space: nowrap;
}

.arrow {
  font-size: 16px;
  color: #666;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 190px;
  width: 100%;
  background-color: #ffffff;
  border: 1px solid #ccc;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  margin-top: 5px;
  z-index: 1000;
  padding: 5px 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dropdown-item:hover {
  background-color: #f3f3f3;
}

.flag-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 10px;
}

.dropdown-item .language-name {
  font-size: 14px;
  color: #333;
}

.flag-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 10px;
}
</style>
